import { loadStripe } from '@stripe/stripe-js'
import { apiRequest } from './util'
import { getStripeCouponId, getStripePriceId } from './prices'
import Sentry from './sentry'
import { toast } from 'react-hot-toast'
import {
  isShuttingDown,
  SHUTDOWN_PURCHASES_DISABLED_MESSAGE,
} from 'lib/shutdown'

let stripePromise = null

// Helper function to get stripe instance with retries
const getStripe = async (retries = 3, delay = 1000) => {
  for (let i = 0; i < retries; i++) {
    try {
      // Initialize new promise if null or previous attempt failed
      if (!stripePromise) {
        stripePromise = loadStripe(
          process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY,
          {
            apiVersion: '2020-08-27', // Keep existing version for compatibility
          }
        )
      }

      const stripe = await stripePromise
      if (stripe) return stripe

      // If we get here, stripe is null but no error was thrown
      // Reset promise and increase delay for next attempt
      stripePromise = null
      await new Promise((resolve) => setTimeout(resolve, delay * (i + 1)))
    } catch (error) {
      Sentry.captureException(error, {
        level: 'error',
        extra: {
          attempt: i + 1,
          maxRetries: retries,
          error: error?.message,
          stripeVersion: '2020-08-27',
        },
      })

      // Reset promise and increase delay for next attempt
      stripePromise = null
      if (i === retries - 1) {
        throw new Error(
          `Failed to initialize Stripe after ${retries} attempts: ${error.message}`
        )
      }
      await new Promise((resolve) => setTimeout(resolve, delay * (i + 1)))
    }
  }
  throw new Error('Failed to initialize Stripe after multiple attempts')
}

export async function redirectToCheckout({
  planId,
  quantity = 1,
  clientReferenceId = null,
  redirectUrl = `${window.location.origin}/thank-you?plan=${planId}`,
  cancelUrl = `${window.location.origin}/dashboard`,
  trial = false,
  replaceHistoryPage = '/settings?tab=manage-plan',
}) {
  // Wind-down: purchases are disabled (the API refuses them too)
  if (isShuttingDown()) {
    toast.error(SHUTDOWN_PURCHASES_DISABLED_MESSAGE)
    return
  }

  try {
    const stripe = await getStripe()
    if (!stripe) {
      throw new Error('Stripe has not been initialized')
    }

    if (!planId) {
      throw new Error('No plan ID provided')
    }

    // Create a checkout session
    const priceId = getStripePriceId(planId)
    if (!priceId) {
      throw new Error(`Invalid plan ID: ${planId}`)
    }

    const couponId = getStripeCouponId(planId)
    const session = await apiRequest('stripe-create-checkout-session', 'POST', {
      coupon: couponId,
      priceId,
      quantity,
      successUrl: redirectUrl,
      cancelUrl: cancelUrl,
      ...(clientReferenceId ? { clientReferenceId } : {}),
      trial,
    })

    if (!session || !session.id) {
      throw new Error('Failed to create checkout session')
    }

    // Store the actual previous page URL in session storage
    sessionStorage.setItem(
      'previousPageUrl',
      window.location.pathname + window.location.search
    )

    // Ensure if user clicks browser back button from checkout they go to #pricing
    // instead of this page or they'll redirect right back to checkout.
    window.history.replaceState({}, '', replaceHistoryPage)

    // Redirect to checkout
    return stripe.redirectToCheckout({
      sessionId: session.id,
    })
  } catch (error) {
    console.error('Checkout error:', error)
    throw error
  }
}

export async function redirectToBilling() {
  // Create a billing session
  const session = await apiRequest('stripe-create-billing-session', 'POST', {
    returnUrl: `${window.location.origin}/settings?tab=manage-plan`,
  })
  // Ensure if user clicks browser back button from billing they go to /settings/general
  // instead of this page or they'll redirect right back to billing.
  window.history.replaceState({}, '', '/settings?tab=manage-plan')

  // Redirect to billing session url
  window.location.href = session.url
}
